import type {
  IntegrationRequestEntry,
  IntegrationView,
  PublicApiScope,
} from "@planerius/contracts/public-api"
import type { WebhookEndpointView, WebhookEventType } from "@planerius/contracts/public-api-webhooks"

import { formatDayMonth, formatTimeOfDay, todayInZone } from "../../lib/format"
import { countOf } from "../../lib/plural"

/**
 * «Разработчикам» (4-В6, 4-В7, 4-В8) — правила без вёрстки.
 *
 * Подписи прав — словами владельца салона, как в макете: решение принимается по тексту
 * «Видит телефоны и почту клиентов полностью», а не по строке clients.pii.
 */

/** Права в мастере доступа — порядок и слова макета 4-В7. */
export const SCOPE_CHOICES: readonly {
  readonly scope: PublicApiScope
  readonly title: string
  readonly sub?: string
  /** Данные клиентов открытым текстом — выделяется цветом. */
  readonly pii?: boolean
}[] = [
  { scope: "catalog.read", title: "Видит услуги и цены" },
  { scope: "slots.read", title: "Видит свободное время" },
  { scope: "appointments.read", title: "Видит записи" },
  { scope: "appointments.write", title: "Создаёт и меняет записи" },
  { scope: "clients.read", title: "Видит карточки клиентов, телефон скрыт: +7 999 ***-**-12" },
  {
    scope: "clients.pii",
    title: "Видит телефоны и почту клиентов полностью",
    sub: "требует акцепта поручения и второго фактора",
    pii: true,
  },
  { scope: "clients.write", title: "Меняет карточки клиентов" },
  { scope: "analytics.read", title: "Видит вашу статистику" },
  { scope: "webhooks.manage", title: "Настраивает уведомления о событиях" },
]

/** Права, для которых нужен акцепт поручения и второй фактор владельца (контракт PII_SCOPES). */
export const PERSONAL_DATA_SCOPES: readonly PublicApiScope[] = [
  "appointments.read",
  "appointments.write",
  "clients.read",
  "clients.write",
  "clients.pii",
]

const SCOPE_SHORT: Readonly<Partial<Record<PublicApiScope, { readonly now: string; readonly past: string }>>> = {
  "catalog.read": { now: "Видит услуги и цены", past: "Видел услуги и цены" },
  "catalog.write": { now: "Меняет услуги", past: "Менял услуги" },
  "schedule.read": { now: "Видит графики", past: "Видел графики" },
  "schedule.write": { now: "Меняет графики", past: "Менял графики" },
  "slots.read": { now: "Видит свободное время", past: "Видел свободное время" },
  "appointments.read": { now: "Видит записи", past: "Видел записи" },
  "appointments.write": { now: "Создаёт и меняет записи", past: "Создавал и менял записи" },
  "clients.read": { now: "Видит карточки клиентов, телефон скрыт", past: "Видел карточки клиентов" },
  "clients.write": { now: "Меняет карточки клиентов", past: "Менял карточки клиентов" },
  "clients.pii": { now: "Видит телефоны и почту клиентов полностью", past: "Видел телефоны клиентов" },
  "analytics.read": { now: "Видит вашу статистику", past: "Видел статистику" },
  "webhooks.manage": { now: "Настраивает уведомления о событиях", past: "Настраивал уведомления" },
}

/** Значки прав в карточке интеграции (4-В6): у отозванной — в прошедшем времени. */
export function scopeBadges(
  scopes: readonly PublicApiScope[],
  revoked: boolean,
): readonly { readonly text: string; readonly pii: boolean }[] {
  return scopes.map((scope) => ({
    text: SCOPE_SHORT[scope]?.[revoked ? "past" : "now"] ?? scope,
    pii: scope === "clients.pii",
  }))
}

export const TERM_CHOICES: readonly { readonly days: number; readonly label: string }[] = [
  { days: 90, label: "90 дней · по умолчанию" },
  { days: 180, label: "180 дней" },
  { days: 365, label: "Год · максимум" },
]

export function needsInstruction(scopes: readonly PublicApiScope[]): boolean {
  return scopes.some((scope) => PERSONAL_DATA_SCOPES.includes(scope))
}

/**
 * Адреса из поля «185.12.44.10, 185.12.44.11»: адрес без маски — один адрес (/32, /128).
 * Неразобранные возвращаются отдельно — отказ называет их, а не «формат неверен».
 */
export function parseAddresses(text: string): { readonly cidrs: readonly string[]; readonly invalid: readonly string[] } {
  const cidrs: string[] = []
  const invalid: string[] = []
  for (const raw of text.split(/[\s,;]+/)) {
    const item = raw.trim()
    if (item.length === 0) continue
    const [address = "", mask] = item.split("/")
    const v4 = /^(\d{1,3})\.(\d{1,3})\.(\d{1,3})\.(\d{1,3})$/.exec(address)
    if (v4 !== null && v4.slice(1).every((part) => Number(part) <= 255)) {
      const bits = mask === undefined ? 32 : Number(mask)
      if (Number.isInteger(bits) && bits >= 0 && bits <= 32) {
        cidrs.push(`${address}/${bits}`)
        continue
      }
    } else if (/^[0-9a-f:]+$/i.test(address) && address.includes(":")) {
      const bits = mask === undefined ? 128 : Number(mask)
      if (Number.isInteger(bits) && bits >= 0 && bits <= 128) {
        cidrs.push(`${address}/${bits}`)
        continue
      }
    }
    invalid.push(item)
  }
  return { cidrs, invalid }
}

export interface AccessDraftForm {
  readonly scopes: readonly PublicApiScope[]
  /** null — все филиалы, включая будущие. */
  readonly branchIds: readonly string[] | null
  readonly days: number
  readonly purpose: string
  readonly addresses: string
  readonly dynamicIpRisk: boolean
}

/** Что мешает перейти к документу — словами макета. null — можно. */
export function draftProblem(form: AccessDraftForm): string | null {
  if (form.scopes.length === 0) return "Отметьте хотя бы одно право."
  if (form.scopes.includes("clients.pii") && !form.scopes.includes("clients.read")) {
    return "Право «Видит телефоны клиентов» нельзя выдать без права «Видит карточки клиентов» — отметьте оба."
  }
  if (form.branchIds !== null && form.branchIds.length === 0) return "Выберите филиал или «Все, включая будущие»."
  if (form.purpose.trim().length < 3) return "Напишите, зачем подрядчику доступ: эта строка войдёт в поручение."
  const parsed = parseAddresses(form.addresses)
  if (parsed.invalid.length > 0) return `Не разобраны адреса: ${parsed.invalid.join(", ")}.`
  if (form.scopes.includes("clients.pii") && parsed.cidrs.length === 0 && !form.dynamicIpRisk) {
    return "Для телефонов клиентов укажите адреса, с которых работает ключ, или отметьте, что адрес плавающий."
  }
  return null
}

export function scopesToggled(scopes: readonly PublicApiScope[], scope: PublicApiScope): readonly PublicApiScope[] {
  return scopes.includes(scope) ? scopes.filter((item) => item !== scope) : [...scopes, scope]
}

// ───────────────────────── карточка интеграции (4-В6) ─────────────────────────

export function integrationBadge(
  view: IntegrationView,
  timeZone: string,
): { readonly text: string; readonly tone: "ok" | "error" | "gray" | "warn" } {
  switch (view.state) {
    case "active":
      return { text: "работает", tone: "ok" }
    case "suspended":
      return { text: "отключена нами", tone: "error" }
    case "revoked":
      return {
        text: view.access?.revokedAt == null ? "доступ отозван" : `доступ отозван ${formatDayMonth(view.access.revokedAt, timeZone)}`,
        tone: "gray",
      }
    case "expired":
      return { text: "срок доступа истёк", tone: "gray" }
    case "no_access":
      return { text: "доступа нет", tone: "warn" }
  }
}

/** «Алексей Громов · 2 июня · поручение draft-2026-10 · 2FA» и «… → отозвал 1 сентября». */
export function grantedLine(view: IntegrationView, timeZone: string): string {
  const access = view.access
  if (access === null) return "—"
  const parts = [access.grantedBy ?? "—", formatDayMonth(access.grantedAt, timeZone)]
  if (access.instruction !== null) {
    parts.push(`поручение ${access.instruction.version}`)
    if (access.instruction.twoFactorConfirmed) parts.push("2FA")
  }
  const line = parts.join(" · ")
  return access.revokedAt === null ? line : `${line} → отозвал${access.revokedBy === null ? "" : ` ${access.revokedBy}`} ${formatDayMonth(access.revokedAt, timeZone)}`
}

/** «312 карточек · 312 с открытыми телефонами». */
export function piiLine(view: IntegrationView): { readonly text: string; readonly tone: "danger" | "ok" | "muted" } {
  const { cards, unmasked } = view.piiReads7d
  const text =
    unmasked > 0
      ? `${countOf(cards, "карточка", "карточки", "карточек")} · ${String(unmasked)} с открытыми телефонами`
      : countOf(cards, "карточка", "карточки", "карточек")
  if (view.state !== "active") return { text, tone: "muted" }
  return { text, tone: unmasked > 0 ? "danger" : "ok" }
}

export function branchesLine(
  branchIds: readonly string[],
  branches: readonly { readonly id: string; readonly name: string }[],
): string {
  if (branchIds.length === 0) return "все, включая будущие"
  const names = branchIds.map((id) => branches.find((branch) => branch.id === id)?.name ?? "филиал")
  return names.length === 1 ? `${names[0] ?? ""} — только одна точка` : names.join(", ")
}

export function untilLine(view: IntegrationView, timeZone: string): string {
  if (view.state !== "active" || view.keysValidUntil === null) return "—"
  return `${formatDayMonth(view.keysValidUntil, timeZone)} ${view.keysValidUntil.slice(0, 4)} · напомним за 2 недели`
}

// ───────────────────────── уведомления о событиях (4-В8) ─────────────────────────

export const EVENT_GROUPS: readonly { readonly title: string; readonly events: readonly WebhookEventType[] }[] = [
  {
    title: "Записи",
    events: [
      "appointment.created",
      "appointment.confirmed",
      "appointment.rescheduled",
      "appointment.cancelled",
      "appointment.completed",
      "appointment.no_show",
    ],
  },
  { title: "Клиенты", events: ["client.created", "client.updated", "client.merged", "client.consent_revoked"] },
  { title: "Каталог и график", events: ["schedule.changed", "service.changed", "employee.changed"] },
]

export const EVENT_LABELS: Readonly<Record<WebhookEventType, string>> = {
  "appointment.created": "запись создана",
  "appointment.confirmed": "запись подтверждена",
  "appointment.rescheduled": "запись перенесена",
  "appointment.cancelled": "запись отменена",
  "appointment.completed": "визит состоялся",
  "appointment.no_show": "клиент не пришёл",
  "client.created": "клиент добавлен",
  "client.updated": "карточка изменена",
  "client.merged": "карточки слиты",
  "client.consent_revoked": "согласие отозвано",
  "schedule.changed": "график изменён",
  "service.changed": "услуга изменена",
  "employee.changed": "мастер изменён",
  "webhook.test": "проверка адреса",
}

export interface HookCard {
  readonly state: string
  readonly tone: "ok" | "warn" | "error" | "gray"
  readonly note: string | null
  readonly noteTone: "danger" | "brand"
  readonly action: { readonly label: string; readonly kind: "pause" | "resume" | "test" } | null
  readonly last: string
}

/** Состояние адреса, пояснение и главное действие — по макету 4-В8. */
export function hookCard(view: WebhookEndpointView, timeZone: string, now: Date = new Date()): HookCard {
  const last = view.lastSuccessAt === null ? "—" : momentWords(view.lastSuccessAt, timeZone, now)
  switch (view.status) {
    case "active":
      return { state: "Работает", tone: "ok", note: null, noteTone: "brand", action: { label: "Приостановить", kind: "pause" }, last }
    case "pending_verification":
      return {
        state: "Проверяем адрес",
        tone: "warn",
        note:
          (view.verificationFailedReason === null ? "" : `${view.verificationFailedReason} `) +
          "Подписку включим, когда адрес ответит успешно — это защита от подписки чужого сервера на поток чужих данных.",
        noteTone: "brand",
        action: { label: "Отправить проверочное ещё раз", kind: "test" },
        last,
      }
    case "paused":
      return { state: "Приостановлено", tone: "gray", note: null, noteTone: "brand", action: { label: "Возобновить", kind: "resume" }, last }
    case "disabled": {
      if (view.disableReason === "manual") {
        return { state: "Отключено", tone: "gray", note: null, noteTone: "brand", action: null, last }
      }
      const since = view.lastSuccessAt === null ? "" : ` с ${formatDayMonth(view.lastSuccessAt, timeZone)}`
      const backlog =
        view.deadDeliveries7d > 0 ? `: ${countOf(view.deadDeliveries7d, "событие", "события", "событий")}` : ""
      return {
        state: view.disableReason === "gone" ? "Отключено: сервер попросил" : "Отключено: сутки не отвечает",
        tone: "error",
        note:
          view.disableReason === "gone"
            ? "Ваш сервер ответил «410 Gone» — так просят прекратить отправку. Если это ошибка, проверьте адрес."
            : `Адрес не отвечает${since}. Отправку остановили, чтобы не копить очередь. Исправьте на своей стороне и нажмите «Проверить адрес» — доставим накопившееся${backlog}.`,
        noteTone: "danger",
        action: { label: "Проверить адрес", kind: "test" },
        last,
      }
    }
  }
}

/** «сегодня, 13:41» или «14 сентября, 09:12». */
export function momentWords(instant: string, timeZone: string, now: Date = new Date()): string {
  const day = new Intl.DateTimeFormat("en-CA", { timeZone, year: "numeric", month: "2-digit", day: "2-digit" }).format(
    new Date(instant),
  )
  const prefix = day === todayInZone(timeZone, now) ? "сегодня" : formatDayMonth(instant, timeZone)
  return `${prefix}, ${formatTimeOfDay(instant, timeZone)}`
}

// ───────────────────────── журнал обращений ─────────────────────────

const PROBLEM_WORDS: Readonly<Record<string, string>> = {
  insufficient_scope: "не хватает прав ключа",
  rate_limited: "упёрлось в лимит запросов",
  too_many_concurrent: "слишком много запросов одновременно",
  not_found: "не найдено",
  malformed_request: "неверные параметры",
  key_expired: "срок ключа истёк",
  key_env_mismatch: "ключ не того окружения",
  daily_quota_exceeded: "дневной лимит исчерпан",
}

/** Строка журнала: «GET /clients/:id · 200 · 34 мс» и причина отказа словами. */
export function requestLine(entry: IntegrationRequestEntry): { readonly route: string; readonly outcome: string; readonly failed: boolean } {
  const route = entry.route.replace(/^\/v1\/public-api/, "")
  const failed = entry.statusCode >= 400
  const reason = entry.problemCode === null ? null : (PROBLEM_WORDS[entry.problemCode] ?? entry.problemCode)
  const outcome = [String(entry.statusCode), reason, `${String(entry.durationMs)} мс`].filter((part) => part !== null).join(" · ")
  return { route: `${entry.method} ${route}`, outcome, failed }
}
